/**
 * Aba Planos de Ação — visões cards, kanban, tabela, calendário e gantt.
 */
import React, { useMemo, useState } from 'react';
import {
  Calendar,
  ClipboardList,
  GanttChart,
  LayoutGrid,
  List,
  Plus,
  Upload,
} from 'lucide-react';
import type { Action, CardData } from '../../types';
import { isActionOverdue } from '../../utils/actionDeadline';
import ActionListView from '../ActionListView';
import {
  ClickableMetric,
  ProjectEmptyState,
  primaryButtonClassName,
  secondaryButtonClassName,
  fmtDate,
} from './projectHubShared';

type ViewMode = 'cards' | 'kanban' | 'table' | 'calendar' | 'gantt';

const VIEW_OPTIONS: Array<{ id: ViewMode; label: string; icon: React.ReactNode }> = [
  { id: 'cards', label: 'Cards', icon: <LayoutGrid size={11} /> },
  { id: 'kanban', label: 'Kanban', icon: <ClipboardList size={11} /> },
  { id: 'table', label: 'Tabela', icon: <List size={11} /> },
  { id: 'calendar', label: 'Calendário', icon: <Calendar size={11} /> },
  { id: 'gantt', label: 'Gantt', icon: <GanttChart size={11} /> },
];

const KANBAN_COLUMNS = [
  { id: 'todo', label: 'A fazer' },
  { id: 'doing', label: 'Em andamento' },
  { id: 'done', label: 'Concluídas' },
] as const;

interface ProjectActionPlansTabProps {
  projectId: string;
  actions: Action[];
  cards: CardData[];
  onCreateAction: () => void;
  onImportPlan: () => void;
  onOpenVsm: () => void;
  onOpenCentral: () => void;
  onNavigateToCard: (cardId: string) => void;
}

function avgLeadTimeDays(actions: Action[]): number {
  const completed = actions.filter(a => a.status === 'done');
  if (completed.length === 0) return 0;
  const total = completed.reduce((sum, action) => {
    const start = new Date(action.createdAt).getTime();
    const end = new Date(action.updatedAt).getTime();
    return sum + Math.max(0, end - start);
  }, 0);
  return Math.round(total / completed.length / 86400000);
}

export const ProjectActionPlansTab: React.FC<ProjectActionPlansTabProps> = ({
  projectId,
  actions,
  cards,
  onCreateAction,
  onImportPlan,
  onOpenVsm,
  onOpenCentral,
  onNavigateToCard,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const activeTab = 'all' as const;

  const kpis = useMemo(() => {
    const open = actions.filter(a => a.status !== 'done' && a.status !== 'canceled').length;
    const overdue = actions.filter(a => isActionOverdue(a)).length;
    const done = actions.filter(a => a.status === 'done').length;
    return { open, overdue, done, leadTime: avgLeadTimeDays(actions) };
  }, [actions]);

  const actionsByKanban = useMemo(() => {
    const grouped: Record<string, Action[]> = { todo: [], doing: [], done: [] };
    for (const action of actions) {
      if (action.status === 'done') grouped.done.push(action);
      else if (action.status === 'doing') grouped.doing.push(action);
      else grouped.todo.push(action);
    }
    return grouped;
  }, [actions]);

  const calendarGroups = useMemo(() => {
    const map = new Map<string, Action[]>();
    for (const action of actions) {
      const due = action.prazo ?? action.deadline;
      const key = due ? fmtDate(due) : 'Sem prazo';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(action);
    }
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === 'Sem prazo') return 1;
      if (b[0] === 'Sem prazo') return -1;
      return new Date(a[0].split('/').reverse().join('-')).getTime() - new Date(b[0].split('/').reverse().join('-')).getTime();
    });
  }, [actions]);

  const ganttItems = useMemo(() => {
    return [...actions]
      .filter(a => a.prazo ?? a.deadline)
      .sort((a, b) => new Date(a.prazo ?? a.deadline!).getTime() - new Date(b.prazo ?? b.deadline!).getTime())
      .slice(0, 20);
  }, [actions]);

  if (actions.length === 0) {
    return (
      <ProjectEmptyState
        icon={<ClipboardList size={28} />}
        title="Nenhum plano de ação vinculado"
        description="Estruture a transformação operacional criando ações, importando um plano existente ou gerando ações a partir do mapa de processos."
        actions={
          <>
            <button type="button" onClick={onCreateAction} className={primaryButtonClassName()}>
              <Plus size={12} />
              Criar ação
            </button>
            <button type="button" onClick={onImportPlan} className={secondaryButtonClassName()}>
              <Upload size={12} />
              Importar plano
            </button>
            <button type="button" onClick={onOpenVsm} className={secondaryButtonClassName()}>
              Gerar pelo VSM
            </button>
          </>
        }
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <ClickableMetric label="Ações abertas" value={kpis.open} />
          <ClickableMetric label="Atrasadas" value={kpis.overdue} tone={kpis.overdue > 0 ? 'danger' : 'default'} />
          <ClickableMetric label="Concluídas" value={kpis.done} tone="success" />
          <ClickableMetric label="Lead Time médio" value={kpis.leadTime > 0 ? `${kpis.leadTime}d` : '—'} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {VIEW_OPTIONS.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => setViewMode(option.id)}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-medium ${
                viewMode === option.id
                  ? 'bg-blue-600 text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
          <button type="button" onClick={onOpenCentral} className={`ml-auto ${secondaryButtonClassName()}`}>
            Central de Ações
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {viewMode === 'table' && (
          <ActionListView
            actions={actions}
            cards={cards}
            onActionClick={() => {}}
            onUpdateAction={() => {}}
            activeTab={activeTab}
            groupBy="none"
            onNavigateToCard={onNavigateToCard}
          />
        )}

        {viewMode === 'cards' && (
          <div className="grid gap-2 p-3 sm:grid-cols-2 xl:grid-cols-3">
            {actions.map(action => (
              <div key={action.id} className="rounded border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
                <p className="line-clamp-2 text-[12px] font-semibold text-slate-800 dark:text-slate-100">{action.title}</p>
                <p className="mt-1 text-[10px] text-slate-500">{action.responsavel ?? action.responsible ?? 'Sem responsável'}</p>
                <div className="mt-2 flex items-center justify-between text-[10px]">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 dark:bg-slate-800">{action.status}</span>
                  <span className={isActionOverdue(action) ? 'text-red-600' : 'text-slate-500'}>
                    {fmtDate(action.prazo ?? action.deadline)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === 'kanban' && (
          <div className="grid h-full min-h-[320px] grid-cols-3 gap-2 p-3">
            {KANBAN_COLUMNS.map(col => (
              <div key={col.id} className="flex min-h-0 flex-col rounded border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                <div className="border-b border-slate-200 px-2 py-1.5 text-[10px] font-semibold uppercase text-slate-600 dark:border-slate-800 dark:text-slate-300">
                  {col.label} ({actionsByKanban[col.id].length})
                </div>
                <div className="flex-1 space-y-1.5 overflow-auto p-2">
                  {actionsByKanban[col.id].map(action => (
                    <div key={action.id} className="rounded border border-slate-200 bg-white p-2 text-[10px] dark:border-slate-700 dark:bg-slate-900">
                      <p className="line-clamp-2 font-medium text-slate-800 dark:text-slate-100">{action.title}</p>
                      <p className="mt-1 text-slate-500">{action.responsavel ?? '—'}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {viewMode === 'calendar' && (
          <div className="space-y-2 p-3">
            {calendarGroups.map(([date, items]) => (
              <div key={date} className="rounded border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-700 dark:border-slate-800 dark:text-slate-200">
                  {date}
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map(action => (
                    <li key={action.id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                      <span className="truncate text-slate-700 dark:text-slate-200">{action.title}</span>
                      <span className="text-slate-500">{action.responsavel ?? '—'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {viewMode === 'gantt' && (
          <div className="p-3">
            {ganttItems.length === 0 ? (
              <p className="text-[11px] text-slate-500">Defina prazos nas ações para visualizar o Gantt.</p>
            ) : (
              <div className="space-y-1">
                {ganttItems.map((action, index) => {
                  const due = new Date(action.prazo ?? action.deadline!);
                  const start = new Date(action.createdAt);
                  const span = Math.max(1, due.getTime() - start.getTime());
                  const width = Math.min(100, Math.max(12, span / 86400000 * 8));
                  return (
                    <div key={action.id} className="grid grid-cols-[180px_1fr_80px] items-center gap-2 text-[10px]">
                      <span className="truncate text-slate-700 dark:text-slate-200">{action.title}</span>
                      <div className="h-5 rounded bg-slate-100 dark:bg-slate-800">
                        <div
                          className={`h-5 rounded ${action.status === 'done' ? 'bg-emerald-500' : isActionOverdue(action) ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${width}%`, marginLeft: `${Math.min(40, index * 3)}%` }}
                        />
                      </div>
                      <span className="font-mono text-slate-500">{fmtDate(action.prazo ?? action.deadline)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
